import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import type { Sport, MarketType, DateFilter, StatusFilter, ViewMode } from './types';
import { SPORT_TABS, MARKET_TYPE_TABS } from './types';

interface DashboardToolbarProps {
  selectedSport: Sport;
  selectedMarketType: MarketType;
  selectedDate: string;
  dateFilter: DateFilter;
  statusFilter: StatusFilter;
  viewMode: ViewMode;
  onSportChange: (sport: Sport) => void;
  onMarketTypeChange: (mt: MarketType) => void;
  onDateChange: (date: string) => void;
  onDatePrev: () => void;
  onDateNext: () => void;
  onDateFilterChange: (f: DateFilter) => void;
  onStatusFilterChange: (f: StatusFilter) => void;
  onViewModeChange: (m: ViewMode) => void;
}

function ChipGroup({ children }: { children: React.ReactNode }) {
  return <div className="bg-muted/50 flex items-center rounded-lg p-1">{children}</div>;
}

function Chip({
  active,
  onClick,
  activeClass = 'bg-primary text-primary-foreground',
  children,
}: {
  active: boolean;
  onClick: () => void;
  activeClass?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
        active ? activeClass : 'text-muted-foreground hover:text-foreground'
      )}
    >
      {children}
    </button>
  );
}

export function DashboardToolbar({
  selectedSport,
  selectedMarketType,
  selectedDate,
  dateFilter,
  statusFilter,
  viewMode,
  onSportChange,
  onMarketTypeChange,
  onDatePrev,
  onDateNext,
  onDateFilterChange,
  onStatusFilterChange,
  onViewModeChange,
}: DashboardToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Sport tabs */}
      <ChipGroup>
        {SPORT_TABS.map((tab) => (
          <Chip key={tab.id} active={selectedSport === tab.id} onClick={() => onSportChange(tab.id)}>
            {tab.label}
          </Chip>
        ))}
      </ChipGroup>

      {/* Market type tabs */}
      <ChipGroup>
        {MARKET_TYPE_TABS.map((tab) => (
          <Chip
            key={tab.id}
            active={selectedMarketType === tab.id}
            onClick={() => onMarketTypeChange(tab.id)}
            activeClass="bg-purple-600 text-white"
          >
            {tab.label}
          </Chip>
        ))}
      </ChipGroup>

      {/* Date picker */}
      <div className="bg-muted/50 flex items-center gap-1 rounded-lg p-1">
        <button onClick={onDatePrev} className="text-muted-foreground hover:text-foreground rounded p-1">
          <ChevronLeft size={16} />
        </button>
        <div className="text-foreground/80 flex items-center gap-1.5 px-2 py-1 text-xs">
          <Calendar size={14} className="text-muted-foreground" />
          <span className="font-mono">{selectedDate}</span>
        </div>
        <button onClick={onDateNext} className="text-muted-foreground hover:text-foreground rounded p-1">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Date filter */}
      <ChipGroup>
        {(['today', 'all'] as DateFilter[]).map((f) => (
          <Chip key={f} active={dateFilter === f} onClick={() => onDateFilterChange(f)}>
            {f === 'today' ? 'Today' : 'All'}
          </Chip>
        ))}
      </ChipGroup>

      {/* Status filter */}
      <ChipGroup>
        {(['active', 'all', 'settled'] as StatusFilter[]).map((f) => (
          <Chip key={f} active={statusFilter === f} onClick={() => onStatusFilterChange(f)} activeClass="bg-primary text-primary-foreground">
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Chip>
        ))}
      </ChipGroup>

      {/* View mode */}
      <ChipGroup>
        <Chip active={viewMode === '2-row'} onClick={() => onViewModeChange('2-row')} activeClass="bg-primary text-primary-foreground">
          2-ROW
        </Chip>
        <Chip active={viewMode === 'classic'} onClick={() => onViewModeChange('classic')} activeClass="bg-primary text-primary-foreground">
          CLASSIC
        </Chip>
      </ChipGroup>
    </div>
  );
}
